import { Modal } from "antd";
import { useContext, useMemo, ReactNode } from "react";
import { DrawerContext } from "../../../store/DrawerContext/DrawerContext";
import {
	ApiOutlined,
	AppstoreOutlined,
	EnvironmentOutlined,
	GlobalOutlined,
	HeartOutlined,
	YoutubeFilled,
} from "@ant-design/icons";
import { DrawerPanelTitle, modalPanelStyles } from "../common/Drawers/DrawerPanelTitle";

type ReleaseScope = 'global' | 'sentiment' | 'competitors' | 'api' | 'qr';

type ReleaseFeature = {
	name: string;
	details: string[];
	date: string;
	scope: ReleaseScope;
	video?: string;
};

const SCOPE_META: Record<ReleaseScope, { label: string; color: string; background: string; border: string; icon: ReactNode }> = {
	global: {
		label: 'Global',
		color: '#334155',
		background: '#f1f5f9',
		border: '#cbd5e1',
		icon: <GlobalOutlined />,
	},
	sentiment: {
		label: 'Sentimiento',
		color: '#9d174d',
		background: '#fce7f3',
		border: '#f9a8d4',
		icon: <HeartOutlined />,
	},
	competitors: {
		label: 'Competidores',
		color: '#0f766e',
		background: '#ccfbf1',
		border: '#5eead4',
		icon: <EnvironmentOutlined />,
	},
	api: {
		label: 'API',
		color: '#1d4ed8',
		background: '#dbeafe',
		border: '#93c5fd',
		icon: <ApiOutlined />,
	},
	qr: {
		label: 'QR Analytics',
		color: '#7c3aed',
		background: '#ede9fe',
		border: '#c4b5fd',
		icon: <AppstoreOutlined />,
	},
};

const FEATURES: Record<string, ReleaseFeature> = {
    REVIEWS_METRICS_OCTOBER_2026: {
        name: 'Métricas de Reputación',
        details: [
            'Gráficos de reseñas, distribución y puntuación mes a mes',
            'Mensaje claro cuando no hay reseñas en el periodo seleccionado',
        ],
        date: 'Octubre 2026',
        scope: 'global',
    },
    LOCAL_SEO_ANALYSIS_OCTOBER_2026: {
        name: 'Análisis autogenerado en SEO Local',
        details: [
            'Correlación de factores SEO Local y medias del TOP 10 por coordenada',
            'Nuevos indicadores: distancia, puntuación, reseñas y keyword en nombre/categoría',
        ],
        date: 'Octubre 2026',
        scope: 'competitors',
    },
    IMPROVE_UX_SEPTEMBER_2026: {
        name: 'Mejoras en el diseño y usabilidad de la plafaforma',
        details: ['Filtros agrupados', 'Mejoras en el selector de la cabecera', 'Standarización del diseño de la plataforma'],
        date: 'Septiembre 2026',
        scope: 'global',
    },
    IMPROVE_GOOGLE_STATUS_SEPTEMBER_2026: {
        name: 'Mejoras en el estado de Google',
        details: ['Cambios externos de Google en en relación a algún campo del local', 'Cambios subidos a Google pendientes de validación', 'Estado de la ficha: Suspendida, duplicada ,pendiente de verificación, etc,...'],
        date: 'Septiembre 2026',
        scope: 'global',
    },
    NEW_ALERTS_AUGUST_2026: {
        name: 'Sistema de alertas con notificaciones por email',
        details: ['Alertas de cambios de Google', 'Alertas de aumento inusual de reseñas negativas', 'Alerta de lanzamiento de publicaciones programadas'],
        date: 'Agosto 2026',
        scope: 'global',
    },
    MASSIVE_LOGS_IMPROVEMENTS_AUGUST_2026: {
		name: 'Mejoras en el registro de operaciones masivas (ver registro)',
		details: ['Aparecen la información del grupo de ubicaciones con el que se lanzó la operación masiva'],
		date: 'Agosto 2026',
		scope: 'global',
	},
    POST_SCHEDULED_EDIT_JULY_2026: {
		name: 'Edición de textos y fechas de las programaciones',
		details: [],
		date: 'Julio 2026',
		scope: 'global',
	},
    NEW_GLOBAL_REPORTS_JULY_2026: {
		name: 'Nuevos sistema de informes que permite envío por email',
		details: ['Informe global de reseñas', 'Informe global de rendimiento', 'Informe por local de reseñas', 'Informe por local de rendimiento'],
		date: 'Julio 2026',
		scope: 'global',
	},
    OBSOLETE_REVIEWS_WITH_AI_JUNE_2026: {
		name: 'Detección de reseñas obsoletas con IA',
		details: ['Cuando un cliente modifica la reseña revisamos con IA si la respuesta es coherente con la nueva puntuación, en caso de no ser así la reseña aparece en naranja pendiente de contestar aunque tenga texto.'],
		date: 'Junio 2026',
		scope: 'global',
	},
	HEADER_JUNE_2026: {
		name: 'Mejoras en los filtros de la cabecera',
		details: ['Permite ver en pantalla todos los grupos activos del proyecto'],
		date: 'Junio 2026',
		scope: 'global',
	},
	PHOTOS_VIDEO_MAYO_2026: {
		name: 'Mejoras en la subida de fotos y videos (no aplica a posts)',
		details: ['Poder subir más de una foto simultáneamente', 'Subida de videos'],
		date: 'Mayo 2026',
		scope: 'global',
	},
	API_MAYO_2026: {
		name: 'Acceso a datos del sistema mediante API',
		details: [],
		date: 'Mayo 2026',
		scope: 'api',
	},
	SENTIMENT_ZONA_ABRIL_2026: {
		name: 'Clasificar regiones utilizando un código de colores',
		details: ['Asignación de regiones a los locales a través de colores', 'Opción de filtrar por zona'],
		date: 'Abril 2026',
		scope: 'global',
	},
	SENTIMENT_REPORT_MARZO_2026: {
		name: 'Envío de informes por email',
		details: ['Motor de creación de informes para envío por email'],
		date: 'Marzo 2026',
		scope: 'global',
	},
	SENTIMENT_IMPROVEMENTS_FEBRERO_2026: {
		name: 'Mejoras en el módulo de sentimiento',
		details: ['Automatización de etiquetado', 'Nubes de palabras para análisis cualitativo'],
		date: 'Febrero 2026',
		scope: 'sentiment',
	},
	GEOGRID_IMPROVEMENTS: {
		name: 'Mejoras en los mapas de competidores',
		details: ['Simplificación de Kpis', 'Créditos pendientes'],
		date: 'Enero 2026',
		scope: 'competitors',
	},
	GEOGRID_LEADS: {
		name: 'Ranking track de leads',
		details: ['Dar de alta leads', 'Ranking track del lead'],
		date: 'Enero 2026',
		scope: 'competitors',
	},
	GEOGRID_CITIES: {
		name: 'Benchmark de competidores por top ciudades',
		details: [
			'Dar de alta competidores',
			'Ranking track de competidores por ciudad',
			'Nuevo mapa de todo el país con info por ciudades',
		],
		date: 'Diciembre 2025',
		scope: 'competitors',
	},
	WIDGETS_CONTACT_POSTS: {
		name: 'Añadidos dos widgets de ejemplo',
		details: ['Widgets de datos de contacto', 'Widget de últimos posts'],
		date: 'Noviembre 2025',
		scope: 'global',
	},
	QA_DEPRECATED: {
		name: 'Preguntas y respuestas eliminado',
		details: ['Google elimina las preguntas de las fichas de GBP'],
		date: 'Noviembre 2025',
		scope: 'global',
	},
	PLAN: {
		name: 'Pantalla de planes en config',
		details: ['Permite ver módulos contratados', 'Videos tutoriales', 'Locales contratados vs activos'],
		date: 'Noviembre 2025',
		scope: 'global',
	},
	POSTS_IMPROVEMENTS: {
		name: 'Mejoras en los posts',
		details: ['Visualización de errores y programaciones por local', 'Añadido filtro por fechas', 'Mejoras de UI'],
		date: 'Noviembre 2025',
		scope: 'global',
	},
	QR_ANALYTICS: {
		name: 'Módulo de QR analytics',
		details: ['Permite monitorizar el número de escaneos de cada empleado'],
		date: 'Octubre 2025',
		scope: 'qr',
	},
	SHIELD: {
		name: 'Escudo para alertar de cambios externos',
		details: ['Nombre, web, teléfonos y horarios'],
		date: 'Octubre 2025',
		scope: 'global',
	},
};

export const ReleaseNotesModal = () => {
	const { releaseNotesModal, setReleaseNotesModal } = useContext(DrawerContext);

	const featuresByDate = useMemo(() => {
		const groups: { date: string; items: ReleaseFeature[] }[] = [];
		Object.values(FEATURES).forEach((feature) => {
			const existing = groups.find((group) => group.date === feature.date);
			if (existing) {
				existing.items.push(feature);
			} else {
				groups.push({ date: feature.date, items: [feature] });
			}
		});
		return groups;
	}, []);

	return (
		<Modal
			open={releaseNotesModal}
			closable={true}
			onCancel={() => setReleaseNotesModal(false)}
			footer={null}
			width="70%"
			className={modalPanelStyles.className}
			bodyStyle={{ ...modalPanelStyles.bodyStyle, minHeight: '90%', paddingTop: 8 }}
			title={
				<DrawerPanelTitle
					title="Release Notes"
					subtitle="Últimas funcionalidades incluidas en la plataforma"
				/>
			}
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
				{featuresByDate.map((group) => (
					<div key={group.date}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
								gap: 12,
								marginBottom: 12,
							}}
						>
							<div style={{ flex: 1, height: 1, background: '#c5cdd6' }} />
							<div
								style={{
									padding: '5px 12px',
									borderRadius: 8,
									background: '#fff',
									border: '1px solid #c5cdd6',
									color: '#1a202c',
									fontSize: 13,
									fontWeight: 700,
									letterSpacing: '0.02em',
									whiteSpace: 'nowrap',
									boxShadow: '0 1px 2px rgba(34, 50, 93, 0.06)',
								}}
							>
								{group.date}
							</div>
						</div>

						<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
							{group.items.map((feature) => {
								const scope = SCOPE_META[feature.scope];
								const isGlobal = feature.scope === 'global';

								return (
									<div
										key={feature.name}
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'flex-start',
											gap: 16,
											padding: '14px 16px',
											background: '#fff',
											border: '1px solid #dbe3ec',
											borderLeft: `3px solid ${isGlobal ? '#94a3b8' : scope.color}`,
											borderRadius: '0 10px 10px 0',
										}}
									>
										<div style={{ flex: 1, minWidth: 0 }}>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
												<span
													style={{
														display: 'inline-flex',
														alignItems: 'center',
														gap: 5,
														padding: '2px 8px',
														borderRadius: 999,
														background: scope.background,
														border: `1px solid ${scope.border}`,
														color: scope.color,
														fontSize: 11,
														fontWeight: 700,
													}}
												>
													{scope.icon}
													{scope.label}
												</span>
												{!isGlobal && (
													<span style={{ fontSize: 11, color: '#94a3b8' }}>
														Módulo concreto
													</span>
												)}
											</div>

											<div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c', lineHeight: 1.35 }}>
												{feature.name}
											</div>

											{feature.details.length > 0 && (
												<ul style={{ margin: '8px 0 0', paddingLeft: 18, color: 'LightSlateGray', fontSize: 13 }}>
													{feature.details.map((detail) => (
														<li key={detail} style={{ marginBottom: 2 }}>
															{detail}
														</li>
													))}
												</ul>
											)}
										</div>

										{feature.video && (
											<a href={feature.video} target="blank" rel="noreferrer" style={{ flexShrink: 0 }}>
												<YoutubeFilled style={{ fontSize: 24, color: 'red' }} />
											</a>
										)}
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</Modal>
	);
};
